import { i18n } from "@lingui/core";
import IconDislike from "@warp-ds/icons/react/dislike-16";
import IconLike from "@warp-ds/icons/react/like-16";
import IconMessage from "@warp-ds/icons/react/message-16";
import { Button } from "@warp-ds/react";
import { useState } from "react";
import { trackSemanticSearchFeedbackClick } from "../../tracking/pulse";
import { postSemanticFeedback } from "../search/semanticClient";

type FeedbackPromptProps = {
	query: string;
	onFeedbackGiven?: () => void;
	locale: string;
};

const CONTAINER_CLASS =
	"flex justify-center items-center content-center gap-24 self-stretch rounded-8 px-32 py-16 s-bg-info-subtle";

function getFollowUpMessage(feedbackType: "positive" | "negative"): string {
	if (feedbackType === "positive") {
		return i18n.t({
			id: "explore.results.feedback.followup.positive",
			message: "Thanks! Do you want to tell us more about what you liked?",
		});
	}
	return i18n.t({
		id: "explore.results.feedback.followup.negative",
		message: "Thanks! Do you want to tell us more about what you didn't like?",
	});
}

export function FeedbackPrompt(props: FeedbackPromptProps) {
	const { query, onFeedbackGiven } = props;
	const [feedbackType, setFeedbackType] = useState<"positive" | "negative" | null>(null);
	const [feedbackUrl, setFeedbackUrl] = useState<string | null>(null);

	const handleInitialFeedback = async (variant: "positive" | "negative") => {
		trackSemanticSearchFeedbackClick(query, variant);
		const feedbackValue = variant === "positive" ? "good" : "bad";
		const url = await postSemanticFeedback(query, feedbackValue);

		if (url) {
			setFeedbackUrl(url);
		}
		setFeedbackType(variant);
	};

	const handleFollowUpClick = () => {
		if (feedbackType) {
			// TODO Add follow up click tracking
			//trackSemanticSearchFeedbackFollowUp(query, feedbackType, "submit");
		}
		onFeedbackGiven?.();
	};

	if (feedbackType) {
		const followUpMessage = getFollowUpMessage(feedbackType);

		return (
			<div className={`${CONTAINER_CLASS} flex-col md:flex-row flex-wrap`}>
				<span className="t4 font-medium">{followUpMessage}</span>
				<div className="flex gap-8">
					<Button
						onClick={handleFollowUpClick}
						className="flex flex-row items-center gap-10 rounded-8 border-none"
						href={feedbackUrl ?? ""}
						target="_blank"
					>
						<IconMessage />
						{i18n.t({
							id: "explore.results.feedback.followup.button",
							message: "Give feedback",
						})}
					</Button>
				</div>
			</div>
		);
	}

	return (
		<div className={`${CONTAINER_CLASS} flex-row md:flex-wrap`}>
			<span className="t4 font-medium">
				{i18n.t({
					id: "explore.results.feedback.prompt",
					message: "What do you think about Explore?",
				})}
			</span>
			<div className="flex gap-8">
				<Button
					onClick={() => handleInitialFeedback("positive")}
					className="p-12 gap-10 rounded-full border-none"
					aria-label={i18n.t({
						id: "explore.results.feedback.positive.label",
						message: "Positive feedback",
					})}
				>
					<IconLike />
				</Button>
				<Button
					onClick={() => handleInitialFeedback("negative")}
					className="p-12 gap-10 rounded-full border-none"
					aria-label={i18n.t({
						id: "explore.results.feedback.negative.label",
						message: "Negative feedback",
					})}
				>
					<IconDislike />
				</Button>
			</div>
		</div>
	);
}
